import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Send, 
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { timesheetAPI } from '../services/api';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';

const Timesheets = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [timesheetIdSearch, setTimesheetIdSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showQuickFilters, setShowQuickFilters] = useState(false);

  // Helper function to safely format dates
  const safeFormatDate = (dateString, formatString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isValid(date)) {
        return format(date, formatString);
      }
      return 'Invalid Date';
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: timesheets, isLoading } = useQuery(
    ['timesheets', statusFilter],
    () => timesheetAPI.getMyTimesheets({ status: statusFilter }),
    {
      select: (response) => {
        const timesheets = response.data.timesheets;
        // Sort by submission date (most recent first)
        return timesheets?.sort((a, b) => {
          // Use submittedAt if available, otherwise use createdAt
          const dateA = new Date(a.submittedAt || a.createdAt);
          const dateB = new Date(b.submittedAt || b.createdAt);
          return dateB - dateA; // Most recent first
        }) || [];
      },
    }
  );

  const deleteMutation = useMutation(
    (timesheetId) => timesheetAPI.deleteTimesheet(timesheetId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('timesheets');
        toast.success('Timesheet deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete timesheet');
      },
    }
  );

  const submitMutation = useMutation(
    (timesheetId) => timesheetAPI.submitTimesheet(timesheetId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('timesheets');
        toast.success('Timesheet submitted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to submit timesheet');
      },
    }
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <FileText className="w-5 h-5 text-gray-500" />;
      case 'SUBMITTED':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'PENDING':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Allow editing for DRAFT (including revoked), PENDING, REUPDATED, and REJECTED timesheets
  const canEdit = (timesheet) => {
    const status = timesheet?.status;
    const isRevokedDraft = status === 'DRAFT' && timesheet?.revokedBy != null;
    return status === 'DRAFT' || status === 'PENDING' || status === 'REUPDATED' || status === 'REJECTED' || isRevokedDraft;
  };
  const canDelete = (status) => status === 'DRAFT' || status === 'SUBMITTED';
  const canSubmit = (status) => status === 'DRAFT';

  const filteredTimesheets = timesheets?.filter(timesheet => {
    const matchesSearch = searchTerm === '' || 
      (timesheet.description && timesheet.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTimesheetId = timesheetIdSearch === '' || 
      (timesheet.id && String(timesheet.id).includes(timesheetIdSearch));
    
    // Date range filtering
    let matchesDateRange = true;
    if (startDate || endDate) {
      const timesheetStartDate = new Date(timesheet.startDate);
      const timesheetEndDate = new Date(timesheet.endDate);
      const filterStartDate = startDate ? new Date(startDate) : null;
      const filterEndDate = endDate ? new Date(endDate) : null;
      
      // Check if dates are valid
      if (isNaN(timesheetStartDate.getTime()) || isNaN(timesheetEndDate.getTime())) {
        matchesDateRange = false;
      } else if (filterStartDate && filterEndDate) {
        // Both dates provided - timesheet should overlap with the range
        const tsStart = new Date(timesheetStartDate.getFullYear(), timesheetStartDate.getMonth(), timesheetStartDate.getDate());
        const tsEnd = new Date(timesheetEndDate.getFullYear(), timesheetEndDate.getMonth(), timesheetEndDate.getDate());
        const fsStart = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), filterStartDate.getDate());
        const fsEnd = new Date(filterEndDate.getFullYear(), filterEndDate.getMonth(), filterEndDate.getDate());
        
        matchesDateRange = tsStart <= fsEnd && tsEnd >= fsStart;
      } else if (filterStartDate) {
        // Only start date - timesheet should end on or after this date
        const tsEnd = new Date(timesheetEndDate.getFullYear(), timesheetEndDate.getMonth(), timesheetEndDate.getDate());
        const fsStart = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), filterStartDate.getDate());
        matchesDateRange = tsEnd >= fsStart;
      } else if (filterEndDate) {
        // Only end date - timesheet should start on or before this date
        const tsStart = new Date(timesheetStartDate.getFullYear(), timesheetStartDate.getMonth(), timesheetStartDate.getDate());
        const fsEnd = new Date(filterEndDate.getFullYear(), filterEndDate.getMonth(), filterEndDate.getDate());
        matchesDateRange = tsStart <= fsEnd;
      }
    }
    
    return matchesSearch && matchesTimesheetId && matchesDateRange;
  });

  const handleDeleteRequest = (timesheet) => {
    setTimesheetToDelete(timesheet);
  };

  const confirmDelete = () => {
    if (!timesheetToDelete) return;
    deleteMutation.mutate(timesheetToDelete.id, {
      onSettled: () => setTimesheetToDelete(null),
    });
  };

  const cancelDelete = () => setTimesheetToDelete(null);

  const handleSubmit = (timesheetId) => {
    if (window.confirm('Are you sure you want to submit this timesheet for approval?')) {
      submitMutation.mutate(timesheetId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header - Mobile Optimized */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0"
      >
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">My Timesheets</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage your timesheet entries</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/timesheets/create')}
          className="btn-primary flex items-center justify-center space-x-2 w-full md:w-auto py-3 md:py-2 shadow-lg md:shadow-none active:scale-95 transition-transform"
        >
          <Plus className="w-5 h-5 md:w-4 md:h-4" />
          <span className="font-semibold md:font-normal">Create Timesheet</span>
        </motion.button>
      </motion.div>

      {/* Quick Status Filters - Mobile Only */}
      <div className="md:hidden">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          <button
            onClick={() => setStatusFilter('')}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              statusFilter === '' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-gray-100 text-gray-700 active:bg-gray-200'
            }`}
          >
            All
          </button>
          {['DRAFT', 'SUBMITTED', 'PENDING', 'APPROVED', 'REJECTED'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                statusFilter === status 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 active:bg-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Filters - Mobile Optimized */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm overflow-hidden"
      >
                {/* Mobile Filter Toggle Button - Enhanced */}
        <div className="md:hidden">
          <button
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="w-full px-4 py-3.5 flex items-center justify-between bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors border border-gray-200 rounded-lg shadow-sm"
          >
            <div className="flex items-center space-x-3">
              <div className="p-1.5 bg-blue-50 rounded-lg">
                <Filter className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex flex-col items-start">
                <span className="font-semibold text-gray-900">Search & Filters</span>
                {(startDate || endDate || searchTerm || timesheetIdSearch) && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    {(startDate ? 1 : 0) + (endDate ? 1 : 0) + (searchTerm ? 1 : 0) + (timesheetIdSearch ? 1 : 0)} active
                  </span>
                )}
              </div>
            </div>
            {mobileFiltersOpen ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Desktop Filters - Always Visible */}
        <div className="hidden md:block p-6">
          <div className="space-y-4">
            {/* Search and Status Filter */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-input pl-10"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by timesheet ID..."
                      value={timesheetIdSearch}
                      onChange={(e) => setTimesheetIdSearch(e.target.value)}
                      className="form-input pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="form-input"
                  >
                    <option value="">All Status</option>
                    <option value="DRAFT">Draft</option>
                    <option value="SUBMITTED">Submitted</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    placeholder="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    placeholder="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setSearchTerm('');
                    setTimesheetIdSearch('');
                    setStatusFilter('');
                  }}
                  className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Filter Summary */}
            {(startDate || endDate || searchTerm || timesheetIdSearch || statusFilter) && (
              <div className="flex items-center space-x-2 flex-wrap">
                <span className="text-sm text-gray-600">
                  Showing {filteredTimesheets?.length || 0} of {timesheets?.length || 0} timesheets
                </span>
                {(startDate || endDate) && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Date: {startDate && endDate ? `${startDate} to ${endDate}` : startDate ? `From ${startDate}` : `Until ${endDate}`}
                  </span>
                )}
                {searchTerm && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    Description: "{searchTerm}"
                  </span>
                )}
                {timesheetIdSearch && (
                  <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                    ID: "{timesheetIdSearch}"
                  </span>
                )}
                {statusFilter && (
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                    Status: {statusFilter}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filters - Collapsible */}
        <AnimatePresence>
          {mobileFiltersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden"
            >
              <div className="px-4 py-4 space-y-4 border-t border-gray-200">
                {/* Quick Search - Enhanced */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                    autoComplete="off"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Timesheet ID Search - Enhanced */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Search by ID..."
                    value={timesheetIdSearch}
                    onChange={(e) => setTimesheetIdSearch(e.target.value.replace(/\D/g, ''))}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                    autoComplete="off"
                  />
                  {timesheetIdSearch && (
                    <button
                      onClick={() => setTimesheetIdSearch('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Quick Date Presets - Mobile */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quick Dates</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date();
                        const startOfWeek = new Date(today);
                        startOfWeek.setDate(today.getDate() - today.getDay());
                        setStartDate(format(startOfWeek, 'yyyy-MM-dd'));
                        setEndDate(format(today, 'yyyy-MM-dd'));
                      }}
                      className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 active:bg-blue-200 transition-colors font-medium"
                    >
                      This Week
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date();
                        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                        setStartDate(format(startOfMonth, 'yyyy-MM-dd'));
                        setEndDate(format(today, 'yyyy-MM-dd'));
                      }}
                      className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 active:bg-blue-200 transition-colors font-medium"
                    >
                      This Month
                    </button>
                  </div>
                </div>

                {/* Date Range - Enhanced */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Clear Button - Enhanced */}
                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setSearchTerm('');
                    setTimesheetIdSearch('');
                    setStatusFilter('');
                  }}
                  className="w-full px-4 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-all shadow-sm"
                >
                  Clear All Filters
                </button>

                {/* Active Filters Summary */}
                {(startDate || endDate || searchTerm || timesheetIdSearch || statusFilter) && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600 mb-2">
                      Showing {filteredTimesheets?.length || 0} of {timesheets?.length || 0} timesheets
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {statusFilter && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                          {statusFilter}
                        </span>
                      )}
                      {searchTerm && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          "{searchTerm.substring(0, 10)}..."
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Timesheets List - Mobile Optimized */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {filteredTimesheets?.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredTimesheets.map((timesheet, index) => (
              <motion.div
                key={timesheet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 md:p-6 hover:bg-gray-50 transition-colors"
              >
                {/* Mobile Card Layout */}
                <div className="md:hidden">
                  <div 
                    className="cursor-pointer"
                    onClick={(e) => {
                      if (!e.target.closest('button')) {
                        navigate(`/timesheets/${timesheet.id}`);
                      }
                    }}
                  >
                    {/* Header Row - Enhanced */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start space-x-3 flex-1 min-w-0" onClick={(e) => {
                        if (!e.target.closest('button')) {
                          navigate(`/timesheets/${timesheet.id}`);
                        }
                      }}>
                        <div className="flex-shrink-0 mt-0.5">
                          {getStatusIcon(timesheet.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-bold text-gray-900 mb-1.5">
                            {safeFormatDate(timesheet.startDate, 'MMM dd')} - {safeFormatDate(timesheet.endDate, 'MMM dd, yyyy')}
                          </h3>
                          <div className="flex items-center space-x-2 flex-wrap gap-1.5">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(timesheet.status)}`}>
                              {timesheet.status}
                            </span>
                            <span className="px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                              ID: {timesheet.id}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Details - Enhanced */}
                    <div className="space-y-2.5 mb-4">
                      <div className="flex items-center text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                        <Clock className="w-4 h-4 mr-2 text-blue-500" />
                        <span className="font-semibold text-gray-900">{timesheet.totalHours} hours</span>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-gray-600">Created {safeFormatDate(timesheet.createdAt, 'MMM dd, yyyy')}</span>
                      </div>
                      {timesheet.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 px-1">
                          {timesheet.description}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons - Mobile Optimized with Better UX */}
                    <div className="grid grid-cols-2 md:hidden gap-2 pt-3 border-t-2 border-gray-200">
                      <button
                        onClick={() => navigate(`/timesheets/${timesheet.id}`)}
                        className="flex flex-col items-center justify-center space-y-1.5 px-3 py-3.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all shadow-sm touch-manipulation"
                      >
                        <Eye className="w-5 h-5" />
                        <span className="text-xs font-bold">View</span>
                      </button>
                      
                      {canEdit(timesheet) && (
                        <button
                          onClick={() => navigate(`/timesheets/${timesheet.id}/edit`)}
                          className="flex flex-col items-center justify-center space-y-1.5 px-3 py-3.5 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 active:bg-green-200 active:scale-95 transition-all shadow-sm touch-manipulation"
                        >
                          <Edit className="w-5 h-5" />
                          <span className="text-xs font-bold">Edit</span>
                        </button>
                      )}
                      
                      {canSubmit(timesheet.status) && (
                        <button
                          onClick={() => handleSubmit(timesheet.id)}
                          disabled={submitMutation.isLoading}
                          className="flex flex-col items-center justify-center space-y-1.5 px-3 py-3.5 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 active:bg-purple-200 active:scale-95 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        >
                          <Send className="w-5 h-5" />
                          <span className="text-xs font-bold">Submit</span>
                        </button>
                      )}
                      
                      {canDelete(timesheet.status) && (
                        <button
                          onClick={() => handleDeleteRequest(timesheet)}
                          disabled={deleteMutation.isLoading}
                          className={`flex flex-col items-center justify-center space-y-1.5 px-3 py-3.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation ${canEdit(timesheet) ? 'col-span-2' : ''}`}
                        >
                          <Trash2 className="w-5 h-5" />
                          <span className="text-xs font-bold">Delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop Card Layout - Unchanged */}
                <div className="hidden md:block cursor-pointer"
                  onClick={(e) => {
                    if (!e.target.closest('button') && !e.target.closest('svg')) {
                      navigate(`/timesheets/${timesheet.id}`);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {getStatusIcon(timesheet.status)}
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-sm font-medium text-gray-900">
                            {safeFormatDate(timesheet.startDate, 'MMM dd')} - {safeFormatDate(timesheet.endDate, 'MMM dd, yyyy')}
                          </h3>
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded border border-gray-300">
                            ID: {timesheet.id}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {timesheet.totalHours} hours • Created {safeFormatDate(timesheet.createdAt, 'MMM dd, yyyy')}
                        </p>
                        {timesheet.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {timesheet.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(timesheet.status)}`}>
                        {timesheet.status}
                      </span>
                      
                      <div className="flex items-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => navigate(`/timesheets/${timesheet.id}`)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                        
                        {canEdit(timesheet) && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => navigate(`/timesheets/${timesheet.id}/edit`)}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </motion.button>
                        )}
                        
                        {canSubmit(timesheet.status) && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleSubmit(timesheet.id)}
                            disabled={submitMutation.isLoading}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                            title="Submit"
                          >
                            <Send className="w-4 h-4" />
                          </motion.button>
                        )}
                        
                        {canDelete(timesheet.status) && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteRequest(timesheet)}
                            disabled={deleteMutation.isLoading}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Rejection/Revocation Reasons - Both Mobile and Desktop */}
                {timesheet.rejectionReason && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">
                      <strong>Rejection Reason:</strong> {timesheet.rejectionReason}
                    </p>
                  </div>
                )}
                
                {timesheet.revocationReason && (
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                    <p className="text-sm text-orange-800">
                      <strong>Revocation Reason:</strong> {timesheet.revocationReason}
                    </p>
                    {timesheet.revokedBy && (
                      <p className="text-xs text-orange-700 mt-1">
                        Revoked by: {timesheet.revokedBy?.firstName || ''} {timesheet.revokedBy?.lastName || ''}
                      </p>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-8 md:p-8 text-center">
            <div className="w-20 h-20 md:w-16 md:h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <FileText className="h-10 w-10 md:h-8 md:w-8 text-gray-400" />
            </div>
            <h3 className="mt-2 md:mt-2 text-lg md:text-sm font-bold text-gray-900">No timesheets found</h3>
            <p className="mt-2 md:mt-1 text-sm text-gray-500 px-4">
              {searchTerm || statusFilter || startDate || endDate
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating a new timesheet.'
              }
            </p>
            {!searchTerm && !statusFilter && !startDate && !endDate && (
              <div className="mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/timesheets/create')}
                  className="btn-primary w-full md:w-auto px-6 py-3 md:py-2 shadow-lg md:shadow-none font-semibold"
                >
                  Create Timesheet
                </motion.button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Floating Action Button - Mobile Only */}
      <div className="md:hidden fixed bottom-6 right-6 z-40">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/timesheets/create')}
          className="w-14 h-14 bg-blue-600 text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-blue-700 active:bg-blue-800 transition-all touch-manipulation"
        >
          <Plus className="w-6 h-6" />
        </motion.button>
      </div>

      {/* Delete Confirmation Modal - Mobile Optimized */}
      {timesheetToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md rounded-xl bg-white shadow-2xl"
          >
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Timesheet Deletion</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-600">
                You are about to delete the timesheet for{' '}
                <span className="font-medium text-gray-900">
                  {safeFormatDate(timesheetToDelete.startDate, 'MMM dd')} – {safeFormatDate(timesheetToDelete.endDate, 'MMM dd, yyyy')}
                </span>
                . This action cannot be undone.
              </p>
              <p className="text-sm text-gray-500">
                Draft and submitted timesheets can be recreated later if needed.
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t-2 border-gray-100 px-4 sm:px-6 py-4">
              <button
                onClick={cancelDelete}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-xl border-2 border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-all text-base sm:text-sm font-semibold touch-manipulation"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isLoading}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 active:bg-red-800 transition-all disabled:opacity-50 text-base sm:text-sm font-semibold shadow-md sm:shadow-none touch-manipulation"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete Timesheet'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Timesheets;

